import { useEffect, useMemo, useState } from "react";
import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
} from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { RedirectTo } from "@/components/auth/redirect";
import { AppShell, ShellSkeleton } from "@/components/shell/app-shell";
import { Wordmark } from "@/components/shell/brand";
import {
  WorkspaceContext,
  lastVisited,
  type WorkspaceSummary,
} from "@/components/shell/workspace-context";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { api, type Id } from "@/lib/api";

export const Route = createFileRoute("/_app/$workspaceSlug")({
  component: WorkspaceLayout,
});

function WorkspaceLayout() {
  const { workspaceSlug } = Route.useParams();
  const location = useLocation();
  const me = useQuery(api.users.me);
  const workspaces = useQuery(api.workspaces.listMine);
  const workspace = workspaces?.find((item) => item.slug === workspaceSlug);
  const projects = useQuery(
    api.projects.list,
    workspace ? { workspaceId: workspace._id } : "skip",
  );

  // Remembered by id, so that when the workspace's address changes (here or
  // from a teammate's settings page) this tab follows it to the new one
  // instead of landing on "not found".
  const [openId, setOpenId] = useState<Id<"workspaces"> | null>(null);
  if (workspace && workspace._id !== openId) setOpenId(workspace._id);
  useEffect(() => {
    if (workspace) lastVisited.setWorkspace(workspace.slug);
  }, [workspace]);

  const value = useMemo(
    () => (me && workspace && workspaces ? { me, workspace, workspaces, projects } : null),
    [me, workspace, workspaces, projects],
  );

  if (me === undefined || workspaces === undefined) return <ShellSkeleton />;

  if (!value) {
    const moved = workspaces.find((item) => item._id === openId);
    if (moved) {
      const rest = location.pathname.split("/").slice(2).join("/");
      return <RedirectTo path={`/${moved.slug}${rest ? `/${rest}` : ""}`} />;
    }
    return <WorkspaceNotFound workspaces={workspaces} />;
  }

  return (
    <WorkspaceContext.Provider value={value}>
      <AppShell>
        <Outlet />
      </AppShell>
    </WorkspaceContext.Provider>
  );
}

function WorkspaceNotFound({ workspaces }: { workspaces: WorkspaceSummary[] }) {
  const { t } = useTranslation("shell");
  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-4 px-6">
      <Wordmark />
      <p className="text-muted-foreground">{t("workspaceNotFound.body")}</p>
      {workspaces.length > 0 ? (
        <ul className="flex flex-col divide-y rounded-lg border">
          {workspaces.map((workspace) => (
            <li key={workspace._id}>
              <Link
                to="/$workspaceSlug"
                params={{ workspaceSlug: workspace.slug }}
                className="flex h-9 items-center gap-2 px-3 transition-colors hover:bg-accent"
              >
                <Avatar name={workspace.name} image={workspace.image} shape="square" />
                <span className="truncate">{workspace.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div>
          <Button asChild>
            <Link to="/new">{t("switcher.create")}</Link>
          </Button>
        </div>
      )}
    </main>
  );
}
